/**
 * Website setup component for revenue tracking.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type RevenueWebsite } from '../api';
import { Notice } from './Panels';

/**
 * Visitor identification mode for the tracking snippet.
 *
 * The server generates the snippet; this component flips the single
 * `var COOKIELESS = ...;` line it contains, so the choice works without an
 * API change. The generator owns that line's exact shape
 * (packages/revenue/src/snippet.ts) — if the format changes, this no-ops and
 * the snippet keeps the server's default rather than breaking.
 */
type PrivacyMode = 'cookieless' | 'cookie';

function withPrivacyMode(snippet: string, mode: PrivacyMode): string {
  return snippet.replace(
    /var COOKIELESS = (?:true|false);/,
    `var COOKIELESS = ${mode === 'cookieless'};`,
  );
}

export function WebsiteSetup() {
  const [websites, setWebsites] = useState<RevenueWebsite[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [selectedWebsite, setSelectedWebsite] = useState<RevenueWebsite | null>(null);
  const [snippet, setSnippet] = useState<string>('');
  const [privacyMode, setPrivacyMode] = useState<PrivacyMode>('cookieless');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const loadWebsites = useCallback(async () => {
    try {
      const data = await api.listRevenueWebsites();
      setWebsites(data.websites);
    } catch (error) {
      setFailure(asFailure(error, 'Could not load your websites.'));
    }
  }, []);

  useEffect(() => { void loadWebsites(); }, [loadWebsites]);

  const createWebsite = async () => {
    if (newDomain.trim() === '') return;
    setLoading(true);
    setFailure(null);
    try {
      await api.createRevenueWebsite(newDomain.trim());
      setNewDomain('');
      await loadWebsites();
    } catch (error) {
      setFailure(asFailure(error, 'Could not add the website.'));
    } finally {
      setLoading(false);
    }
  };

  const loadSnippet = async (website: RevenueWebsite) => {
    setSelectedWebsite(website);
    setFailure(null);
    try {
      const data = await api.getRevenueSnippet(website.id);
      setSnippet(data.snippet);
    } catch (error) {
      setFailure(asFailure(error, 'Could not load the tracking snippet.'));
    }
  };

  const displaySnippet = withPrivacyMode(snippet, privacyMode);

  const copySnippet = async () => {
    await navigator.clipboard.writeText(displaySnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const deleteWebsite = async (websiteId: string) => {
    setConfirmingDelete(null);
    setFailure(null);
    try {
      await api.deleteRevenueWebsite(websiteId);
      if (selectedWebsite?.id === websiteId) {
        setSelectedWebsite(null);
        setSnippet('');
      }
      await loadWebsites();
    } catch (error) {
      setFailure(asFailure(error, 'Could not delete the website.'));
    }
  };

  return (
    <div className="page">
      <header className="intro">
        <h2>Website Setup</h2>
        <p>Add your website to start tracking visitors, sessions, and revenue attribution.</p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <section className="card">
        <header><h2>Add Website</h2></header>
        <div className="row">
          <label className="field grow">
            <span>Domain</span>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              onKeyDown={(e) => e.key === 'Enter' && void createWebsite()}
            />
          </label>
          <button className="primary" onClick={() => void createWebsite()} disabled={loading || newDomain.trim() === ''}>
            {loading ? 'Adding...' : 'Add Website'}
          </button>
        </div>
      </section>

      {websites.length > 0 && (
        <section className="card">
          <header><h2>Your Websites</h2></header>
          <table className="data-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Tracking ID</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {websites.map((website) => (
                <tr key={website.id}>
                  <td style={{ fontWeight: 500 }}>{website.domain}</td>
                  <td><code className="badge">{website.trackingId}</code></td>
                  <td className="muted">{new Date(website.createdAt).toLocaleDateString()}</td>
                  <td>
                    {confirmingDelete === website.id ? (
                      <div className="row tight">
                        <span className="muted" style={{ fontSize: 12 }}>Delete this website and all its tracking data?</span>
                        <button className="ghost tiny danger" onClick={() => void deleteWebsite(website.id)}>Confirm</button>
                        <button className="ghost tiny" onClick={() => setConfirmingDelete(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div className="row tight">
                        <button className="ghost tiny" onClick={() => void loadSnippet(website)}>Snippet</button>
                        <button className="ghost tiny danger" onClick={() => setConfirmingDelete(website.id)}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {selectedWebsite !== null && snippet !== '' && (
        <section className="card">
          <header>
            <h2>Tracking Snippet — {selectedWebsite.domain}</h2>
            <button className="ghost tiny" onClick={() => void copySnippet()}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </header>
          <p className="muted">Add this snippet to the &lt;head&gt; of every page you want to track.</p>

          <label className="check">
            <input
              type="checkbox"
              checked={privacyMode === 'cookieless'}
              onChange={(e) => setPrivacyMode(e.target.checked ? 'cookieless' : 'cookie')}
            />
            Cookieless tracking (recommended)
          </label>
          <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {privacyMode === 'cookieless'
              ? 'No cookie is set: visitors are identified by a value that rotates every 24 hours, so no consent banner is needed in most jurisdictions. The trade-off: unique visitors are counted per day, so cross-day uniques are less accurate.'
              : 'A first-party cookie identifies visitors for 365 days, giving accurate cross-day unique counts. The trade-off: this typically requires a cookie consent banner.'}
            {' '}The snippet always honours Do Not Track and <code>window.__dvOptOut</code>.
          </p>

          <pre className="code-block"><code>{displaySnippet}</code></pre>
        </section>
      )}
    </div>
  );
}

function asFailure(error: unknown, fallback: string): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure(fallback, 'Try again.', 0);
}
